import { useQuery } from '@tanstack/react-query';
import { PAGINATION, type Paginated, type RankDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * The whole rank master, once, for every picker that needs it.
 *
 * [P2] Extracted from `RankPicker` because the post-type editor needs a MULTI-select
 * over the same rows. `RankPicker` did not gain a `mode` prop: cardinality is
 * not a mode — it is a different control with a different `onChange` signature, a
 * different `allowClear` meaning and a different placeholder, and adding the prop would
 * force a discriminated props union plus a cast at the AntD boundary onto a component
 * four call sites already depend on.
 *
 * One query key, so TanStack dedupes the two consumers into one fetch.
 *
 * Ordered by seniority ascending, which is what the server's default sort already does —
 * and the reason `listRanksQuerySchema` overrides the shared `sortOrder` default from
 * `desc` to `asc`.
 *
 * ⚠️ [amended RANKS] ONE BOUNDED PAGE IS NOW AN ASSUMPTION RATHER THAN AN OBSERVATION.
 * This said "fourteen rows, so one page covers the whole master" and the client's
 * establishment list took it to FORTY, against a `PAGINATION.MAX_LIMIT` of 100. The
 * fetch is unchanged and still correct — but the master is no longer obviously smaller
 * than a page, and nothing here would notice the day it stops being. Those sixty rows
 * are the entire safety margin, and past them every consumer loses its most JUNIOR
 * ranks first, silently, because the sort is seniority ascending.
 */
export type RankOption = { value: string; label: string };

/**
 * ⚠️ [D4.5 §1b] IT RETURNS BOTH ANSWERS, AND THAT IS THE LOAD-BEARING PART.
 *
 * `options` is what may be CHOSEN — active ranks only, which is what every picker offered
 * before and must keep offering. `resolve` is what an ALREADY-STORED id renders as, and it
 * reaches deactivated rows too. They are different questions and this hook used to answer
 * only the first, so a post type or a command role naming a since-deactivated rank rendered
 * that rank's ObjectId in the editor (`lib/selectOptions.ts` has the mechanism).
 *
 * ⚠️ THE FETCH DROPPED `isActive: true` RATHER THAN GAINING A SECOND QUERY. `listRanks`
 * applies no `is_active` filter when the key is absent, and one
 * bounded page already covered it — so this costs nothing and the split happens here, where
 * both answers can be kept in step. A second query would be a second cache entry that can
 * disagree with the first.
 *
 * This is `lib/policyColumns.ts`'s `postRows`-versus-`allPostTypes` shape, second instance.
 */
export const useRankOptions = (): {
  ranks: RankDto[];
  options: RankOption[];
  /**
   * ⚠️ [amended C1f §8d] THE FOURTEEN RANKS THAT MAY HOLD A SUPERVISORY SEAT.
   *
   * A SECOND answer beside `options`, never a narrowing of it — `lib/policyColumns.ts`'s
   * `postRows`-versus-`allPostTypes` split, third instance. `resolve` still reaches EVERY
   * rank, so a policy that stored a rank outside this set before C1f goes on rendering that
   * rank's NAME rather than an ObjectId. That is what makes the narrowing safe on live data.
   *
   * ⚠️ AND IT IS FILTERED HERE RATHER THAN ON THE WIRE. `listRanksQuerySchema` gains no field
   * and `rank.model.ts` gains no index: this hook already fetches the whole master in one
   * bounded page, and §7 ships an index with the query that needs one.
   */
  commandOptions: RankOption[];
  byId: Map<string, RankDto>;
  resolve: (id: string) => { name: string } | undefined;
  isFetching: boolean;
} => {
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.ranks.options(),
    queryFn: () => api.get<Paginated<RankDto>>('/ranks', { query: { limit: PAGINATION.MAX_LIMIT } }),
    staleTime: 5 * 60_000,
  });

  const ranks = data?.items ?? [];
  const byId = new Map(ranks.map((rank) => [rank.id, rank]));
  const labelOf = (rank: RankDto): string => `${rank.name} (${rank.shortName})`;

  return {
    ranks,
    options: ranks.filter((rank) => rank.isActive).map((rank) => ({ value: rank.id, label: labelOf(rank) })),
    commandOptions: ranks
      .filter((rank) => rank.isActive && rank.isCommandEligible)
      .map((rank) => ({ value: rank.id, label: labelOf(rank) })),
    byId,
    resolve: (id) => {
      const rank = byId.get(id);
      /* ⚠️ The label carries the short name, so a fallback built from `name` alone would
         read differently from every live option and look like data rather than absence. */
      return rank ? { name: labelOf(rank) } : undefined;
    },
    isFetching,
  };
};
